<script setup lang="ts">
import {
  UiIconButton,
  UiProgressBar,
  UiSegmentedControl,
} from "@packages/component-library";
import { IconCloseRegular } from "@packages/icon-library";
import VueQrcode from "@chenfengyuan/vue-qrcode";
import {
  computed,
  ComputedRef,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  watch,
} from "vue";
import { ITotp, useUserStore } from "@/modules/user-module";
import { TOTP } from "totp-generator";
import { EarnDeductSkeleton } from "@/modules/app-mobile-nav";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["close"]);
const { t } = useI18n();
const userStore = useUserStore();
const cardNumber: ComputedRef<null | string> = computed(
  () => userStore.cardNumber
);
const userTotp: ComputedRef<null | ITotp> = computed(() => userStore.userTotp);
const selectTab = ref("deduct");
const tabs = [
  { id: "deduct", title: t("mobileNav.qrModal.deduct") },
  { id: "earn", title: t("mobileNav.qrModal.earn") },
];

const timer = ref(0);
const timerInterval: null | any = ref(null);
const totpCode = ref("");
const totpOptions = ref<any>({
  digits: 0,
  algorithm: "SHA-512",
  period: 0,
});

const qrValue = computed(() =>
  selectTab.value === "deduct"
    ? cardNumber.value + totpCode.value
    : cardNumber.value
);

const formattedTotp = computed(
  () => `${totpCode.value.slice(0, 3)} ${totpCode.value.slice(-3)}`
);

const progressBarValue = computed(
  () => ((timer.value % totpOptions.value.period) / 60) * 100
);

watch(timer, (newValue) => {
  if (newValue % totpOptions.value.period) {
    totpCode.value = TOTP.generate(
      userTotp.value?.secret,
      totpOptions.value
    ).otp;
  }
});

onBeforeMount(() => {
  userStore.loadUserTotp().then(() => {
    timerInterval.value = setInterval(() => {
      timer.value = Math.trunc(new Date().getTime() / 1000);
    }, 1000);

    totpOptions.value.digits = userTotp.value?.digits;
    totpOptions.value.period = userTotp.value?.interval;

    totpCode.value = TOTP.generate(
      userTotp.value?.secret,
      totpOptions.value
    ).otp;
  });
});

onBeforeUnmount(() => {
  clearInterval(timerInterval.value);
  userStore.clearUserTotp();
});
</script>

<template>
  <div class="earn-deduct">
    <template v-if="userTotp && totpCode">
      <div class="earn-deduct__qr">
        <VueQrcode
          :value="qrValue"
          tag="svg"
          :options="{ width: 220, margin: 0, errorCorrectionLevel: 'H' }"
        />

        <div class="earn-deduct__progress">
          <template v-if="selectTab === 'deduct'">
            <UiProgressBar :value="progressBarValue" size="m" />
            <p>{{ t("mobileNav.qrModal.updateInfo") }}</p>
          </template>
        </div>

        <UiSegmentedControl v-model="selectTab" :segments="tabs" />
      </div>

      <p v-if="selectTab === 'earn'">{{ t("mobileNav.qrModal.showCode") }}</p>

      <p v-else>
        {{ t("mobileNav.qrModal.paymentCode") }}
        <strong>{{ formattedTotp }}</strong>
      </p>

      <UiIconButton size="m" color="primary" @click="emit('close')">
        <IconCloseRegular />
      </UiIconButton>
    </template>

    <EarnDeductSkeleton v-else @close="emit('close')" />
  </div>
</template>

<style scoped lang="scss">
.earn-deduct {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 150;
  background-color: var(--color-non-semantic-background-yellow);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: column;

  &__qr {
    border-radius: 12px;
    background-color: var(--color-semantic-background-normal-bg-primary);
    padding: calc($base-space * 8) ($base-space * 6);
    display: flex;
    flex-flow: column;
    justify-content: center;
    align-items: center;

    > .ui-segments {
      margin: calc($base-space * 8) 0 0;

      width: 220px;

      &:deep(button) {
        width: 100%;
      }
    }
  }

  &__progress {
    width: 220px;
    min-height: 58px;
    padding: calc($base-space * 6) 0 0;
    display: flex;
    flex-flow: column;
    justify-content: center;
    align-items: center;
    gap: calc($base-space * 2);
    @include body-s();
  }

  > p {
    margin: calc($base-space * 8) 0 0;
    max-width: 170px;
    color: var(--color-semantic-content-normal-secondary);
    text-align: center;
    display: flex;
    flex-flow: column;
    align-items: center;
    justify-content: center;
    gap: $base-space;
    @include body-m();

    > strong {
      @include body-bold-m();
    }
  }

  > button {
    margin: calc($base-space * 6) 0 0;
  }
}
</style>
